import { useParams } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import CategoryTab from '@/components/base/CategoryTab';
import { categories } from '@/mocks/categories';
import { navigationLinks } from '@/mocks/navigationLinks';
import { articles } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const links = navigationLinks[currentCategory.slug] || [];
  const categoryArticles = articles.filter((a) => a.category === currentCategory.slug);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 爱情岛论坛导航`,
    description: currentCategory.description,
    url: `https://aiqingdao-navigation.com/category/${currentCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '爱情岛论坛导航',
      url: 'https://aiqingdao-navigation.com/',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://aiqingdao-navigation.com/' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name },
    ],
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'online': return { text: '在线', cls: 'bg-accent-100 text-accent-700' };
      case 'slow': return { text: '较慢', cls: 'bg-primary-100 text-primary-700' };
      case 'offline': return { text: '暂不可用', cls: 'bg-foreground-200 text-foreground-600' };
      default: return { text: '未知', cls: 'bg-foreground-200 text-foreground-600' };
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${currentCategory.name} - 爱情岛论坛导航`}
        description={currentCategory.description}
        keywords={`${currentCategory.name},爱情岛论坛,论坛导航`}
        canonicalUrl={`https://aiqingdao-navigation.com/category/${currentCategory.slug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="container-custom">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory.name },
              ]}
            />
            <div className="pb-8">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className={`${currentCategory.icon} text-2xl text-primary-600`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">
                    {currentCategory.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-custom py-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
            {categories.map((cat) => (
              <CategoryTab
                key={cat.slug}
                {...cat}
                isActive={cat.slug === currentCategory.slug}
                linkCount={(navigationLinks[cat.slug] || []).length}
              />
            ))}
          </div>
        </div>

        <section className="container-custom pb-16">
          <h2 className="font-heading text-xl text-foreground-900 mb-6">
            入口链接（共 {links.length} 个）
          </h2>
          <div className="space-y-3">
            {links.map((link) => {
              const st = statusLabel(link.status);
              return (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className={`flex items-center gap-4 p-4 rounded-lg border transition-all duration-300 no-underline ${
                    link.status === 'offline'
                      ? 'bg-background-100 border-background-200/70 opacity-60 pointer-events-none'
                      : 'bg-background-50 border-background-200/70 hover:border-primary-200'
                  }`}
                >
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-background-100">
                    <i className="ri-link text-lg text-foreground-500"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="font-heading text-base text-foreground-900 truncate">
                        {link.title}
                      </h3>
                      <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${st.cls} whitespace-nowrap`}>
                        {st.text}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-500 truncate">
                      {link.description}
                      {link.speed && <span className="ml-2">· 速度：{link.speed}</span>}
                    </p>
                  </div>
                  {link.status !== 'offline' && (
                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 opacity-0 hover:opacity-100 transition-opacity">
                      <i className="ri-external-link-line text-base"></i>
                    </div>
                  )}
                </a>
              );
            })}
          </div>

          {links.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无入口链接</p>
            </div>
          )}
        </section>

        {categoryArticles.length > 0 && (
          <section className="bg-background-100 py-16">
            <div className="container-custom">
              <h2 className="font-heading text-xl text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} {...article} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}